import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { QRBlock } from "@/components/QRCode";
import { PrivacyPromise } from "@/components/privacy/PrivacyPromise";
import { SectionLoader, EmptyState } from "@/components/common";
import { initials } from "@/lib/format";
import { UserPlus, Plus, Loader2, Ban, Users2, Clock, CheckCircle2, ShieldCheck } from "lucide-react";

const origin = () => (typeof window !== "undefined" ? window.location.origin : "");

export default function InviteMember() {
  const [data, setData] = useState(null);
  const [label, setLabel] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/invites/mine");
      setData(res.data);
    } catch (e) {
      toast.error("Could not load your invites");
      setData({ invites: [], people: [], joined_count: 0, pending_count: 0 });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setCreating(true);
    try {
      await api.post("/invites/mine", { label: label.trim() || null });
      setLabel("");
      toast.success("Invite created");
      await load();
    } catch (e) {
      toast.error("Could not create invite");
    } finally {
      setCreating(false);
    }
  };

  const deactivate = async (id) => {
    try {
      await api.delete(`/invites/mine/${id}`);
      toast.success("Invite deactivated");
      await load();
    } catch (e) {
      toast.error("Could not deactivate invite");
    }
  };

  if (!data) return <SectionLoader label="Loading your invites…" />;

  const activeInvites = (data.invites || []).filter((i) => i.active);

  return (
    <div className="space-y-6" data-testid="invite-member-page">
      <div>
        <h1 className="font-serif text-2xl font-semibold text-foreground sm:text-3xl">Invite a member</h1>
        <p className="mt-1 text-muted-foreground">Share your personal link or QR code to bring someone into Friday Conversations.</p>
      </div>

      <Card className="border-primary/30 bg-primary/5"><CardContent className="flex items-start gap-3 p-4">
        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
        <p className="text-sm text-foreground">
          Anyone who joins through your link is credited to you. An organizer reviews and approves each request before access is granted.
        </p>
      </CardContent></Card>

      <PrivacyPromise />

      <div className="grid grid-cols-2 gap-4 sm:max-w-md">
        <Card className="border-border bg-card"><CardContent className="flex items-center gap-3 p-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10"><CheckCircle2 className="h-5 w-5 text-primary" /></div>
          <div><p className="text-xs text-muted-foreground">Joined</p><p className="font-serif text-xl font-semibold tabular-nums text-foreground" data-testid="invite-joined-count">{data.joined_count}</p></div>
        </CardContent></Card>
        <Card className="border-border bg-card"><CardContent className="flex items-center gap-3 p-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary"><Clock className="h-5 w-5 text-muted-foreground" /></div>
          <div><p className="text-xs text-muted-foreground">Pending approval</p><p className="font-serif text-xl font-semibold tabular-nums text-foreground" data-testid="invite-pending-count">{data.pending_count}</p></div>
        </CardContent></Card>
      </div>

      <Card className="border-border bg-card"><CardContent className="p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <Label htmlFor="inv-label">Create another invite (optional label)</Label>
            <Input id="inv-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Coffee with Sam" className="mt-1.5" data-testid="invite-label-input" />
            <p className="mt-1 text-xs text-muted-foreground">Labels help you track which link brought someone in.</p>
          </div>
          <Button onClick={create} disabled={creating} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="invite-create-button">
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1.5 h-4 w-4" />Create invite</>}
          </Button>
        </div>
      </CardContent></Card>

      <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
        {activeInvites.map((inv) => {
          const url = `${origin()}/register?invite=${inv.invite_id}`;
          return (
            <Card key={inv.invite_id} className="border-border bg-card" data-testid="my-invite-card">
              <CardContent className="flex flex-col items-center p-5">
                <div className="mb-2 flex w-full items-center justify-between gap-2">
                  <p className="line-clamp-1 text-sm font-semibold text-foreground" title={inv.label || "Personal invite"}>{inv.label || "Personal invite"}</p>
                  <Badge variant="secondary" className="shrink-0"><Users2 className="mr-1 h-3 w-3" />{inv.signup_count} joined</Badge>
                </div>
                <QRBlock value={url} size={150} fileName={`my-invite-${inv.invite_id}`} printTitle="Join Friday Conversations" printSubtitle={inv.label || "Scan to request membership"} testId={`my-invite-qr-${inv.invite_id}`} />
                {data.invites.length > 1 && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="mt-1 text-muted-foreground hover:text-destructive" data-testid="invite-deactivate"><Ban className="mr-1.5 h-4 w-4" />Deactivate</Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Deactivate this invite?</AlertDialogTitle><AlertDialogDescription>The QR/link will stop working for new requests.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => deactivate(inv.invite_id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Deactivate</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div>
        <h2 className="font-serif text-xl font-semibold text-foreground">People you’ve invited</h2>
        {(data.people || []).length === 0 ? (
          <div className="mt-3"><EmptyState icon={UserPlus} title="No one yet" description="Share your link or QR to bring people into Friday Conversations." testId="invite-people-empty" /></div>
        ) : (
          <Card className="mt-3 border-border bg-card"><CardContent className="divide-y divide-border p-0">
            {data.people.map((p, idx) => (
              <div key={idx} className="flex items-center justify-between gap-3 p-3" data-testid="invited-person-row">
                <div className="flex items-center gap-3">
                  <Avatar className="h-9 w-9"><AvatarImage src={p.avatar || undefined} /><AvatarFallback className="bg-primary text-xs text-primary-foreground">{initials(p.name)}</AvatarFallback></Avatar>
                  <span className="text-sm font-medium text-foreground">{p.name}</span>
                </div>
                {p.status === "active" ? (
                  <Badge className="border border-emerald-200 bg-emerald-50 text-emerald-800"><CheckCircle2 className="mr-1 h-3 w-3" />Member</Badge>
                ) : p.status === "pending" ? (
                  <Badge variant="secondary"><Clock className="mr-1 h-3 w-3" />Pending</Badge>
                ) : (
                  <Badge variant="secondary">Declined</Badge>
                )}
              </div>
            ))}
          </CardContent></Card>
        )}
      </div>
    </div>
  );
}
